import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser, syncUserData, clearError } from '../../redux/slices/authSlice';
import logger from '../../utils/logger';
import './LoginModal.css';
import trackingManager from '../../services/trackingManager';
import languageManager from '../../utils/languageManager';

const LoginModal = ({ visible, onClose, onShowRegister, onShowResetPassword }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { loading, syncing, error } = useSelector(state => state.auth);
  const [messageApi, contextHolder] = message.useMessage();
  const [activeTab, setActiveTab] = useState('wechat'); // 默认显示微信登录tab
  
  // 当弹窗打开时清除之前的错误信息
  useEffect(() => {
    if (visible) {
      dispatch(clearError());
      // 每次打开弹窗时重置为微信登录tab
      setActiveTab('wechat');
    }
  }, [visible, dispatch]);

  // 处理登录表单提交
  const handleSubmit = async (values) => {
    const hide = messageApi.loading(languageManager.t('auth.loggingIn'), 0);
    try {
      // 上报用户登录埋点
      trackingManager.reportUserAuth('LOGIN', values.email);
      
      // 执行登录
      await dispatch(loginUser(values)).unwrap();
      hide();
      messageApi.success(languageManager.t('auth.loginSuccess'));
      
      // 关闭登录框并立即开始同步
      onClose();
      
      // 立即开始同步数据，不再使用setTimeout
      const syncHide = messageApi.loading(languageManager.t('auth.syncingData'), 0);
      try {
        await dispatch(syncUserData()).unwrap();
        syncHide();
        messageApi.success(languageManager.t('auth.dataSyncComplete'));
      } catch (error) {
        syncHide();
        messageApi.warning(languageManager.t('auth.partialSyncFailed'));
        logger.error('数据同步失败:', error);
      }
    } catch (error) {
      hide();
      messageApi.error(error || languageManager.t('auth.loginFailed'));
    }
  };

  // 去注册页面
  const handleGoToRegister = () => {
    form.resetFields();
    dispatch(clearError());
    onShowRegister();
  };

  // 去忘记密码页面
  const handleGoToResetPassword = () => {
    form.resetFields();
    dispatch(clearError());
    onShowResetPassword();
  };

  // 渲染微信登录tab内容（二维码）
  const renderWeChatTab = () => (
    <div className="wechat-login-content">
      <div className="wechat-qrcode-container">
        <img 
          src={require('../../assets/wechat_gzh.jpg')}
          alt="微信公众号二维码"
          className="wechat-qrcode-large"
        />
      </div>
      <div className="wechat-login-tips">
        {languageManager.t('auth.wechatLoginTips') || '微信扫一扫，关注公众号后即可登录/注册'}
      </div>
    </div>
  );

  // 渲染邮箱登录tab内容
  const renderEmailTab = () => (
    <Form
      form={form}
      name="loginForm"
      onFinish={handleSubmit}
      scrollToFirstError
      layout="vertical"
      className="email-login-form"
    >
      {error && (
        <div className="login-error-message">
          {error}
        </div>
      )}
      
      <Form.Item
        name="email"
        rules={[
          { required: true, message: languageManager.t('auth.enterEmail') },
          { type: 'email', message: languageManager.t('auth.enterValidEmail') }
        ]}
      >
        <Input 
          prefix={<UserOutlined />} 
          placeholder={languageManager.t('auth.email')} 
          size="large"
          autoComplete="email"
        />
      </Form.Item>

      <Form.Item
        name="password"
        rules={[{ required: true, message: languageManager.t('auth.enterPassword') }]}
      >
        <Input.Password 
          prefix={<LockOutlined />} 
          placeholder={languageManager.t('auth.password')} 
          size="large"
          autoComplete="current-password"
        />
      </Form.Item>

      <Form.Item>
        <Button 
          type="primary" 
          htmlType="submit" 
          size="large" 
          block
          loading={loading || syncing}
        >
          {syncing ? languageManager.t('auth.syncingData') : languageManager.t('auth.login')}
        </Button>
      </Form.Item>

      <div className="login-form-footer">
        <Button type="link" onClick={handleGoToResetPassword}>
          {languageManager.t('auth.forgotPassword')}
        </Button>
        <Button type="link" onClick={handleGoToRegister}>
          {languageManager.t('auth.registerNow')}
        </Button>
      </div>
    </Form>
  );

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={() => {
        form.resetFields();
        dispatch(clearError());
        onClose();
      }}
      footer={null}
      maskClosable={false}
      centered
      className="login-modal"
      closeIcon={<span className="login-modal-close">×</span>}
    >
      {contextHolder}
      
      {/* Tab切换器 */}
      <div className="login-tab-switcher">
        <button
          className={`login-tab ${activeTab === 'wechat' ? 'login-tab-active' : ''}`}
          onClick={() => setActiveTab('wechat')}
        >
          {languageManager.t('auth.wechatLogin') || '微信登录'}
        </button>
        <button
          className={`login-tab ${activeTab === 'email' ? 'login-tab-active' : ''}`}
          onClick={() => setActiveTab('email')}
        >
          {languageManager.t('auth.emailLogin') || '邮箱登录'}
        </button>
      </div>

      {/* Tab内容 */}
      <div className="login-tab-content">
        {activeTab === 'wechat' ? renderWeChatTab() : renderEmailTab()}
      </div>
    </Modal>
  );
};

export default LoginModal; 